/**
 * Catalog workspace — unified entry for the article catalog,
 * categories, and product groups.
 *
 * Two visual modes on one URL:
 *   - Tree (default): nested Category → Product Group → Article rows
 *   - Flat: flat article list (auto-engaged when sorting any column)
 *
 * Detail mode is engaged by ``/catalog/articles/{id}`` — the right
 * pane opens, the table compresses. Create mode is engaged by
 * ``/catalog/{entity}/new`` — the right pane shows the create form.
 *
 * Drag-drop, sort, filter, search, expansion state, and revenue window
 * are all encoded in the URL query string (REPLACE history). Selection
 * lives in the path (PUSH history).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndProvider,
  useDragDropManager,
  useDragLayer,
} from "react-dnd";
import { MultiBackend } from "react-dnd-multi-backend";
import { HTML5toTouch } from "rdndmb-html5-to-touch";

import type { DragItem } from "../../components/CatalogDnd";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Input,
  InputNumber,
  Popover,
  Segmented,
  Select,
  Space,
  Switch,
  Tooltip,
  Typography,
} from "antd";
import {
  PlusOutlined,
  SettingOutlined,
  UndoOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  ApiError,
  api,
  type CatalogStockResponse,
  type CategoryRow,
  type ChainRow,
  type ProductGroupRow,
  type RecipeRow,
  type RevenueWindow,
  type StockArticleEntry,
} from "../../api/client";
import {
  DATA_COLUMN_LABELS,
  DATA_COLUMN_ORDER,
  detectPreset,
  isLowStock,
  needsStockFetch,
  readStoredView,
  resolveVisibleDataCols,
  writeStoredView,
  type CatalogViewConfig,
  type LowStockRule,
} from "../../utils/catalogView";
import { CatalogRightPane } from "../../components/CatalogRightPane";
import { CatalogTable, type FlatRow } from "../../components/CatalogTable";
import {
  applyRange,
  decodeMulti,
  decodeRange,
  useTwoPaneLayout,
  useWorkspaceUrlState,
} from "../../components/workspace";
import { UncategorisedBucket } from "../../components/UncategorisedBucket";
import { emitCatalogMutation } from "../../components/UncategorisedBadge";

const { Text } = Typography;

type WindowStats = {
  revenue: number;
  profit: number;
  units: number;
};

type RevenueMap = {
  articles: Map<number, WindowStats>;
  groups: Map<number, WindowStats & { anchor: string | null }>;
  categories: Map<number, WindowStats & { anchor: string | null }>;
};

const EMPTY_REVENUE: RevenueMap = {
  articles: new Map(),
  groups: new Map(),
  categories: new Map(),
};

export const CatalogWorkspace = () => (
  // ``MultiBackend`` switches between HTML5 (mouse / pointer) and
  // TouchBackend (touch) at runtime: a single ``touchstart`` event
  // promotes the current gesture to the touch backend, a ``mousedown``
  // keeps it on HTML5. This is the canonical way to make react-dnd
  // work on phones / tablets without changing any of the consumer
  // hooks — the spec each row registers via ``useDrag``/``useDrop``
  // is backend-agnostic. The ``HTML5toTouch`` preset wires both up.
  <DndProvider backend={MultiBackend} options={HTML5toTouch}>
    <CatalogWorkspaceInner />
  </DndProvider>
);

const CatalogWorkspaceInner = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state, push, replace, queryString } = useWorkspaceUrlState();

  // Live drag state — used for "collapse rows under the dragged
  // entity" so groups/articles get out of the way visually while you
  // drop a category somewhere else.
  //
  // CRITICAL: do NOT include ``clientOffset`` in this selector — it
  // changes on every pointer move (~60Hz) and would re-render the
  // workspace + table on every frame. AntD's inline ``components.body.row``
  // gets treated as a new component on each render, remounting every
  // row's DOM and killing the in-progress drag operation. Autoscroll
  // reads the offset directly from the DnD monitor in a sibling
  // component below.
  const drag = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem() as DragItem | null,
  }));

  // Single-pane detection + container measurement is owned by
  // ``useTwoPaneLayout``. It observes the catalog scroll container
  // (drives container-aware column visibility in the table) and the
  // always-visible outer flex parent (drives single-pane vs two-pane).
  // See the hook for the rationale on observing both.
  const {
    leftScrollRef,
    workspaceOuterRef,
    isMobile,
    catalogContainerWidth,
  } = useTwoPaneLayout();

  const [rows, setRows] = useState<ChainRow[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [groups, setGroups] = useState<ProductGroupRow[]>([]);
  // Recipes keyed by their article's chain root — drives the catalog "recipe"
  // badge + the in-pane recipe editor so recipes are managed in the catalogue.
  const [recipes, setRecipes] = useState<RecipeRow[]>([]);
  const [revenue, setRevenue] = useState<RevenueMap>(EMPTY_REVENUE);
  const [stock, setStock] = useState<Map<number, StockArticleEntry>>(new Map());
  const [, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The catalog view config (column preset + picker visibility + the
  // low-stock highlight) lives in the workspace URL state; localStorage
  // seeds it on a fresh visit (see effects below).
  const viewCfg: CatalogViewConfig = useMemo(
    () => ({
      preset: state.preset,
      cols: state.cols,
      hlOn: state.hlOn,
      hlRule: state.hlRule,
      hlPct: state.hlPct,
    }),
    [state.preset, state.cols, state.hlOn, state.hlRule, state.hlPct],
  );
  const visibleDataCols = useMemo(
    () => resolveVisibleDataCols(viewCfg),
    [viewCfg],
  );
  const stockNeeded = useMemo(() => needsStockFetch(viewCfg), [viewCfg]);

  // Seed view config from localStorage ONLY when the URL carries none of
  // the view params (URL is authoritative). Runs once on mount.
  useEffect(() => {
    const hasUrlView = ["view", "cols", "hl", "hlrule", "hlpct"].some((kk) =>
      searchParams.has(kk),
    );
    if (hasUrlView) return;
    const stored = readStoredView();
    if (!stored) return;
    replace({
      preset: stored.preset,
      cols: stored.cols,
      hlOn: stored.hlOn,
      hlRule: stored.hlRule,
      hlPct: stored.hlPct,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Mirror the effective view config back to localStorage on every change.
  useEffect(() => {
    writeStoredView(viewCfg);
  }, [viewCfg]);

  // Stage the search input so each keystroke doesn't replace the URL.
  const [qDraft, setQDraft] = useState(state.q);
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (qDraft !== state.q) replace({ q: qDraft });
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDraft]);
  // External resets (browser back/forward, deep-link) change state.q
  // without going through the input — mirror it back so the box doesn't
  // show stale text. qDraft is intentionally omitted from deps (the
  // debounce loop above mirrors input → URL; this mirrors URL → input).
  useEffect(() => {
    if (state.q !== qDraft) setQDraft(state.q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.q]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch with include_inactive so deactivated cats + groups land
      // in the response. The workspace partitions them into the
      // bottom "INACTIVE" bucket and lets the operator reach the
      // right-pane Reactivate button without first guessing the row
      // exists. Without this, deactivated entries are invisible AND
      // their FK-attached articles/groups become unreachable.
      const [r, c, g, rec] = await Promise.all([
        api.listArticleChains(),
        api.listCategories(true),
        api.listProductGroups({ include_inactive: true }),
        api.listRecipes().catch(() => [] as RecipeRow[]), // best-effort badge/editor
      ]);
      setRows(r);
      setCategories(c);
      setGroups(g);
      setRecipes(rec);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshRevenue = useCallback(async () => {
    try {
      const r = await api.getCatalogRevenue({
        window: state.window,
        from: state.windowFrom ?? undefined,
        to: state.windowTo ?? undefined,
      });
      setRevenue({
        articles: new Map(
          r.articles.map((a) => [
            a.article_chain_root_id,
            {
              revenue: a.revenue_cents,
              profit: a.profit_cents,
              units: a.units_sold,
            },
          ]),
        ),
        groups: new Map(
          r.groups.map((g) => [
            g.product_group_id,
            {
              revenue: g.revenue_cents,
              profit: g.profit_cents,
              units: g.units_sold,
              anchor: g.anchor,
            },
          ]),
        ),
        categories: new Map(
          r.categories.map((c) => [
            c.category_id,
            {
              revenue: c.revenue_cents,
              profit: c.profit_cents,
              units: c.units_sold,
              anchor: c.anchor,
            },
          ]),
        ),
      });
    } catch {
      setRevenue(EMPTY_REVENUE);
    }
  }, [state.window, state.windowFrom, state.windowTo]);

  // Lazy stock fetch: only when a stock column is visible or the
  // low-stock highlight is on (see needsStockFetch). Uses the same
  // revenue window so refilled/sold are window-scoped consistently.
  const refreshStock = useCallback(async () => {
    if (!stockNeeded) {
      setStock(new Map());
      return;
    }
    if (state.window === "custom" && (!state.windowFrom || !state.windowTo)) {
      return; // wait for both custom dates (backend 400s otherwise)
    }
    try {
      const r: CatalogStockResponse = await api.getCatalogStock({
        window: state.window,
        from: state.windowFrom ?? undefined,
        to: state.windowTo ?? undefined,
      });
      setStock(new Map(r.articles.map((a) => [a.article_chain_root_id, a])));
    } catch {
      // The stock map is decorative here (drives optional columns + the
      // colour highlight); swallow so a transient stock-endpoint failure
      // doesn't blank the catalog. Rows simply render without stock data.
      setStock(new Map());
    }
  }, [stockNeeded, state.window, state.windowFrom, state.windowTo]);

  useEffect(() => {
    void refresh();
  }, [refresh]);
  useEffect(() => {
    void refreshRevenue();
  }, [refreshRevenue]);
  useEffect(() => {
    void refreshStock();
  }, [refreshStock]);

  const onChanged = useCallback(() => {
    void refresh();
    void refreshRevenue();
    void refreshStock();
    emitCatalogMutation();
  }, [refresh, refreshRevenue, refreshStock]);

  // chain_root_id → its recipe (if the article is one). Drives the table badge
  // + the in-pane recipe editor.
  const recipeByRoot = useMemo(() => {
    const m = new Map<number, RecipeRow>();
    for (const r of recipes) m.set(r.article_chain_root_id, r);
    return m;
  }, [recipes]);

  // -------------------------------------------------------------------
  // URL → pane mode
  // -------------------------------------------------------------------

  // Focus state: /catalog/categories/:id or /catalog/groups/:id (non-new).
  // Focus is NOT pane state — it just scrolls + highlights a row in the
  // table. The pane remains closed unless an article is also selected.
  const focusKey = useMemo<string | null>(() => {
    if (params.categoryId && params.categoryId !== "new") {
      return `cat:${params.categoryId}`;
    }
    if (params.groupId && params.groupId !== "new") {
      return `grp:${params.groupId}`;
    }
    return null;
  }, [params.categoryId, params.groupId]);

  // Scroll + flash highlight on focus change.
  useEffect(() => {
    if (!focusKey) return;
    // Wait one frame so the table has rendered the focused row.
    const handle = window.requestAnimationFrame(() => {
      const el = document.querySelector(
        `[data-row-key="${CSS.escape(focusKey)}"]`,
      ) as HTMLElement | null;
      if (!el) return;
      el.scrollIntoView({ block: "start", behavior: "smooth" });
      el.classList.add("catalog-row-focus-flash");
      window.setTimeout(() => el.classList.remove("catalog-row-focus-flash"), 2200);
    });
    return () => window.cancelAnimationFrame(handle);
  }, [focusKey, rows, categories, groups]);

  const paneMode = useMemo(() => {
    // ``params`` is shaped by App.tsx routes — only one of the *Id /
    // *New params is set at a time. Order matters: ``new`` matches the
    // literal ``new`` segment, the numeric matchers run afterwards.
    if (params.articleId === "new") {
      return {
        kind: "article-create" as const,
        prefill: {
          category: searchParams.get("category")
            ? Number(searchParams.get("category"))
            : undefined,
          group: searchParams.get("group")
            ? Number(searchParams.get("group"))
            : undefined,
        },
      };
    }
    if (params.categoryId === "new") {
      return { kind: "category-create" as const };
    }
    if (params.groupId === "new") {
      return {
        kind: "group-create" as const,
        prefill: {
          category: searchParams.get("category")
            ? Number(searchParams.get("category"))
            : undefined,
        },
      };
    }
    if (params.articleId) {
      return { kind: "article-view" as const, articleId: Number(params.articleId) };
    }
    if (params.categoryId) {
      return {
        kind: "category-edit" as const,
        categoryId: Number(params.categoryId),
      };
    }
    if (params.groupId) {
      return { kind: "group-edit" as const, groupId: Number(params.groupId) };
    }
    return null;
  }, [params.articleId, params.categoryId, params.groupId, searchParams]);

  // The right pane being open used to force a "compressed" 2-column
  // table view. That made wide screens feel cramped — the table area is
  // still ~viewport - pane_width wide and easily fits the full responsive
  // column set. We now always render the full table and let AntD's
  // ``responsive`` breakpoints + horizontal scroll handle the geometry.
  const compressed = false;
  const selectedArticleId =
    paneMode?.kind === "article-view" ? paneMode.articleId : null;
  const selectedCategoryId =
    paneMode?.kind === "category-edit" ? paneMode.categoryId : null;
  const selectedGroupId =
    paneMode?.kind === "group-edit" ? paneMode.groupId : null;

  // -------------------------------------------------------------------
  // Filter rows by search box, then build tree/flat data source
  // -------------------------------------------------------------------

  const filteredArticles = useMemo(() => {
    const q = state.q.trim().toLowerCase();
    const nameContains = state.filters.name?.toLowerCase() ?? "";
    const sellRange = decodeRange(state.filters.sell);
    const buyRange = decodeRange(state.filters.buy);
    const marginRange = decodeRange(state.filters.margin);
    const profitRange = decodeRange(state.filters.profit);
    const expectedRange = decodeRange(state.filters.expected);
    const unitWhitelist = decodeMulti(state.filters.unit);
    const mismatchOnly = state.filters.mismatch === "on";
    // "Show deleted" toggle — defaults to ON so the operator notices
    // soft-deleted chains (DELETED badge in the name cell). Explicitly
    // setting "0" hides them. URL semantics: absent filter == on,
    // "0" == off; any other value also reads as on (forward-compatible
    // with a future "show-deleted-but-only-X" variant).
    // Backend already returns inactive chains in the chains view
    // (articles.py: list_articles fetches active=None there).
    const showDeleted = state.filters.show_deleted !== "0";

    return rows.filter((r) => {
      // Active / deleted gate first — cheapest predicate, biggest cut.
      if (!showDeleted && !r.active) return false;
      // Global search box (across multiple fields).
      if (q) {
        if (
          !r.name.toLowerCase().includes(q) &&
          !r.barcodes.some((b) => b.barcode.toLowerCase().includes(q)) &&
          !r.product_group_name?.toLowerCase().includes(q) &&
          !r.category_name?.toLowerCase().includes(q)
        ) {
          return false;
        }
      }
      // Per-column filters.
      if (nameContains && !r.name.toLowerCase().includes(nameContains)) return false;
      if (!applyRange(r.sell_price_cents / 100, sellRange)) return false;
      if (!applyRange(r.buy_price_cents !== null ? r.buy_price_cents / 100 : null, buyRange))
        return false;
      const marginPct =
        r.margin_cents !== null && r.sell_price_cents > 0
          ? Math.round((r.margin_cents / r.sell_price_cents) * 100)
          : null;
      if (!applyRange(marginPct, marginRange)) return false;
      const profit = revenue.articles.get(r.chain_root_id)?.profit ?? null;
      if (!applyRange(profit !== null ? profit / 100 : null, profitRange))
        return false;
      // Expected + mismatch are stock-derived; until stock joins the row,
      // applyRange against zero/missing data would reject every row when
      // the filter is set. Skip if no stock data is present.
      // (Future: thread expected/mismatch into ChainRow.)
      if (
        unitWhitelist.length > 0 &&
        !unitWhitelist.includes(
          (r.unit_type_override ?? r.unit_type ?? "") as string,
        )
      ) {
        return false;
      }
      // Mismatch toggle: with no stock data yet, we can't meaningfully
      // evaluate this — leave the filter active in URL state but make
      // it a no-op until stock columns are populated.
      void mismatchOnly;
      void expectedRange;
      return true;
    });
  }, [rows, state.q, state.filters, revenue.articles]);

  // Drift bucket bypasses the column-filter pipeline — it's a
  // "needs attention" indicator, not part of the categorised tree.
  // Otherwise a stray Mismatch / Sell / Margin filter would hide drift
  // articles even though the nav badge insists they exist.
  const driftRows = useMemo(
    () => rows.filter((r) => r.category_id === null),
    [rows],
  );

  const flatRows: FlatRow[] = useMemo(() => {
    const out: FlatRow[] = [];
    // NOTE: do NOT hide/remove rows while a drag is in flight. React's
    // keyed reconciliation moves DOM nodes when rows above the dragged
    // source disappear — and the browser cancels the native HTML5 drag
    // the instant the source <tr> is detached/moved mid-gesture. That
    // broke reorder for every group/category whose DOM position would
    // shift when the lane "cleaned up" (i.e. every row past the first).
    // Layout stays put; drop targets are still disambiguated by the
    // canDropOn() check on each row.
    // Low-stock predicate for a single article. Reused for article rows
    // AND for the category/group rollup (which only counts currently-
    // filtered members, since the member arrays below come from
    // filteredArticles).
    const articleIsLow = (r: typeof rows[number]): boolean =>
      isLowStock(viewCfg, {
        currentStock: r.expected_qty,
        packSize: r.pack_size,
        sinceDeliveryBaselineQty:
          stock.get(r.chain_root_id)?.since_delivery_baseline_qty ?? null,
      });
    const articleStats = (r: typeof rows[number]) => {
      const s = revenue.articles.get(r.chain_root_id);
      return {
        revenue_cents: s?.revenue ?? null,
        profit_cents: s?.profit ?? null,
        stock: stock.get(r.chain_root_id) ?? null,
        isLow: articleIsLow(r),
      };
    };
    if (state.mode === "flat") {
      for (const r of filteredArticles) {
        if (r.category_id === null) continue; // drift bucket handled separately
        out.push({
          kind: "article",
          key: `article:${r.chain_root_id}`,
          article: r,
          ...articleStats(r),
          expected: r.expected_qty,
          mismatch: r.mismatch_qty,
        });
      }
      out.sort(buildFlatComparator(state.sort, state.sortDir));
      return out;
    }

    // Tree mode — category → group → article, with sort_order respected.
    // Soft-deleted chains (article.active === false) are pulled out of
    // their original category and bucketed under a virtual DELETED
    // section appended at the very end of the tree. Keeps the live
    // catalog focused on active inventory while still letting the
    // operator audit deleted entries.
    const activeArticlesForTree = filteredArticles.filter((r) => r.active);
    const deletedArticlesForTree = filteredArticles.filter((r) => !r.active);
    // Partition categories so active ones render in their normal slots
    // and inactive ones are bucketed under a virtual INACTIVE header at
    // the very bottom. Operators can still see groups + articles stuck
    // inside an inactive category (so they can drag them out before
    // reactivating) and the right-pane Reactivate button stays
    // reachable.
    const sortedCats = [...categories].sort(
      (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name),
    );
    const cats = sortedCats.filter((c) => c.active);
    const inactiveCats = sortedCats.filter((c) => !c.active);
    // Shared body for both active and inactive category renders. The
    // only branch is whether to skip an empty cat when a search query
    // is active: for active cats we hide them (they're noise during
    // search); for inactive cats we always show them so the operator
    // can audit + move stuck content out before reactivating.
    const pushCatSection = (
      cat: CategoryRow,
      options: { hideEmptyOnSearch: boolean },
    ): void => {
      const catCollapsed = state.collapsed.has(`cat:${cat.id}`);
      const catArticles = activeArticlesForTree.filter(
        (r) => r.category_id === cat.id,
      );
      if (
        options.hideEmptyOnSearch &&
        catArticles.length === 0 &&
        state.q
      )
        return;
      const catStats = revenue.categories.get(cat.id);
      const catExpected = catArticles.reduce((sum, a) => sum + a.expected_qty, 0);
      const catMismatch = catArticles.some((a) => a.mismatch_qty !== null)
        ? catArticles.reduce(
            (sum, a) => sum + (a.mismatch_qty ?? 0),
            0,
          )
        : null;
      out.push({
        kind: "category",
        key: `cat:${cat.id}`,
        category: cat,
        collapsed: catCollapsed,
        member_count: catArticles.length,
        revenue_cents: catStats?.revenue ?? 0,
        profit_cents: catStats?.profit ?? 0,
        expected: catExpected,
        mismatch: catMismatch,
      });
      if (catCollapsed && !state.q) return;
      const catGroups = groups
        .filter((g) => g.category_id === cat.id)
        .sort(
          (a, b) =>
            a.sort_order - b.sort_order || a.name.localeCompare(b.name),
        );
      for (const grp of catGroups) {
        const grpCollapsed = state.collapsed.has(`grp:${grp.id}`);
        const grpArticles = catArticles.filter(
          (r) => r.product_group_id === grp.id,
        );
        if (grpArticles.length === 0 && state.q) continue;
        const grpStats = revenue.groups.get(grp.id);
        const grpExpected = grpArticles.reduce(
          (sum, a) => sum + a.expected_qty,
          0,
        );
        const grpMismatch = grpArticles.some((a) => a.mismatch_qty !== null)
          ? grpArticles.reduce((sum, a) => sum + (a.mismatch_qty ?? 0), 0)
          : null;
        out.push({
          kind: "group",
          key: `grp:${grp.id}`,
          group: grp,
          collapsed: grpCollapsed,
          member_count: grpArticles.length,
          revenue_cents: grpStats?.revenue ?? 0,
          profit_cents: grpStats?.profit ?? 0,
          expected: grpExpected,
          mismatch: grpMismatch,
          sharedUnit: grpArticles.every(
            (r) => r.unit_type === grpArticles[0].unit_type,
          )
            ? (grpArticles[0]?.unit_type ?? null)
            : null,
        });
        if (grpCollapsed && !state.q) continue;
        if (grpArticles.length === 0) {
          out.push({
            kind: "placeholder",
            key: `placeholder:${grp.id}`,
            product_group_id: grp.id,
            category_id: cat.id,
          });
          continue;
        }
        for (const r of grpArticles.sort((a, b) =>
          a.name.localeCompare(b.name),
        )) {
          out.push({
            kind: "article",
            key: `article:${r.chain_root_id}`,
            article: r,
            ...articleStats(r),
            expected: r.expected_qty,
            mismatch: r.mismatch_qty,
          });
        }
      }
      const ungrouped = catArticles.filter((r) => r.product_group_id === null);
      if (ungrouped.length > 0) {
        for (const r of ungrouped.sort((a, b) => a.name.localeCompare(b.name))) {
          out.push({
            kind: "article",
            key: `article:${r.chain_root_id}`,
            article: r,
            ...articleStats(r),
            expected: r.expected_qty,
            mismatch: r.mismatch_qty,
            ungroupedInCategory: true,
          });
        }
      }
    };
    for (const cat of cats) {
      pushCatSection(cat, { hideEmptyOnSearch: true });
    }
    // Virtual DELETED bucket at the bottom. Visible only when "Show
    // deleted" is on (filteredArticles is already gated upstream by
    // that switch) and there's at least one soft-deleted chain.
    if (deletedArticlesForTree.length > 0) {
      out.push({
        kind: "deleted-header",
        key: "deleted-header",
        member_count: deletedArticlesForTree.length,
        profit_cents: null,
        revenue_cents: null,
        expected: null,
        mismatch: null,
      });
      const sortedDeleted = [...deletedArticlesForTree].sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      for (const r of sortedDeleted) {
        out.push({
          kind: "article",
          key: `article:${r.chain_root_id}`,
          article: r,
          ...articleStats(r),
          expected: r.expected_qty,
          mismatch: r.mismatch_qty,
          // Reuse the ungrouped-in-category visual treatment so the
          // article rows under DELETED render at a slight indent under
          // the virtual header.
          ungroupedInCategory: true,
        });
      }
    }
    // Virtual INACTIVE bucket below DELETED. Holds soft-deactivated
    // categories with everything still attached to them (groups,
    // articles, even inactive groups elsewhere). Operator can click a
    // category row to open the right pane and hit Reactivate; while
    // it's here, articles inside remain draggable so they can be
    // moved out into a live category if the operator prefers to
    // retire the cat with the content already relocated.
    if (inactiveCats.length > 0) {
      const inactiveMemberCount = inactiveCats.reduce((sum, cat) => {
        return (
          sum +
          activeArticlesForTree.filter((r) => r.category_id === cat.id).length
        );
      }, 0);
      out.push({
        kind: "inactive-header",
        key: "inactive-header",
        member_count: inactiveMemberCount,
        profit_cents: null,
        revenue_cents: null,
        expected: null,
        mismatch: null,
      });
      for (const cat of inactiveCats) {
        pushCatSection(cat, { hideEmptyOnSearch: false });
      }
    }
    return out;
  }, [
    filteredArticles,
    categories,
    groups,
    revenue,
    stock,
    viewCfg,
    state.mode,
    state.collapsed,
    state.q,
    state.sort,
    state.sortDir,
  ]);

  // -------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------

  const onSelectArticle = useCallback(
    (chainRootId: number) => {
      push({}, `/catalog/articles/${chainRootId}`);
    },
    [push],
  );

  const onSelectCategory = useCallback(
    (categoryId: number) => {
      push({}, `/catalog/categories/${categoryId}`);
    },
    [push],
  );

  const onSelectGroup = useCallback(
    (groupId: number) => {
      push({}, `/catalog/groups/${groupId}`);
    },
    [push],
  );

  const onSortChange = useCallback(
    (column: string | null, dir: "asc" | "desc") => {
      // Sorting an article-level column auto-flips Tree → Flat per spec.
      if (column && state.mode === "tree") {
        replace({ sort: column, sortDir: dir, mode: "flat" });
      } else {
        replace({ sort: column, sortDir: dir });
      }
    },
    [replace, state.mode],
  );

  const onToggleCollapse = useCallback(
    (key: string) => {
      const next = new Set(state.collapsed);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      replace({ collapsed: next });
    },
    [replace, state.collapsed],
  );

  const onAutoExpand = useCallback(
    (key: string) => {
      if (!state.collapsed.has(key)) return;
      const next = new Set(state.collapsed);
      next.delete(key);
      replace({ collapsed: next });
    },
    [replace, state.collapsed],
  );

  const onWindowChange = useCallback(
    (window: RevenueWindow) => {
      replace({ window });
    },
    [replace],
  );

  const onModeChange = useCallback(
    (mode: "tree" | "flat") => {
      // Switching back to Tree clears the active sort (per spec — sorted
      // by margin doesn't make sense in a category-nested view).
      if (mode === "tree" && state.sort) {
        replace({ mode, sort: null });
      } else {
        replace({ mode });
      }
    },
    [replace, state.sort],
  );

  const onAddArticleToGroup = useCallback(
    (categoryId: number, groupId: number) => {
      navigate(
        `/catalog/articles/new?category=${categoryId}&group=${groupId}`,
      );
    },
    [navigate],
  );

  const onAddGroupToCategory = useCallback(
    (categoryId: number) => {
      navigate(`/catalog/groups/new?category=${categoryId}`);
    },
    [navigate],
  );

  const onReorderCategories = useCallback(
    async (orderedIds: number[]) => {
      try {
        await api.reorderCategories(orderedIds);
        onChanged();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      }
    },
    [onChanged],
  );

  const onReorderGroups = useCallback(
    async (categoryId: number, orderedGroupIds: number[]) => {
      try {
        await api.reorderProductGroups(categoryId, orderedGroupIds);
        onChanged();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      }
    },
    [onChanged],
  );

  const onMoveGroupToCategory = useCallback(
    async (
      groupId: number,
      targetCategoryId: number,
      insertBeforeGroupId: number | null,
      targetCategoryGroupOrder: number[],
    ) => {
      try {
        // 1. PATCH category — cascades article category_id on the
        //    backend so every article in this group moves with it.
        await api.updateProductGroup(groupId, {
          category_id: targetCategoryId,
        });
        // 2. Insert the group at the right slot in the new category's
        //    order. We use the table's trusted snapshot of the target
        //    category's group order (same snapshot used for
        //    same-category reorders) rather than re-deriving it from
        //    the workspace's ``groups`` prop — that derivation broke
        //    when the target category had no groups yet (empty list
        //    after filtering) and when local sort_order had drifted
        //    from the rendered order. Drop the moved group from its
        //    old slot in the snapshot (if present — it might be in a
        //    different category, in which case it's already absent),
        //    then splice it before ``insertBeforeGroupId`` or append.
        const targetOrder = targetCategoryGroupOrder.filter(
          (id) => id !== groupId,
        );
        if (insertBeforeGroupId === null) {
          targetOrder.push(groupId);
        } else {
          const idx = targetOrder.indexOf(insertBeforeGroupId);
          if (idx === -1) {
            // Target group disappeared (deleted by another client) or
            // the snapshot was stale. Append rather than fail; log so we
            // can spot this in dev if it starts firing unexpectedly.
            console.warn(
              "[catalog] insertBeforeGroupId %d not found in target category %d order; appending",
              insertBeforeGroupId,
              targetCategoryId,
            );
            targetOrder.push(groupId);
          } else {
            targetOrder.splice(idx, 0, groupId);
          }
        }
        await api.reorderProductGroups(targetCategoryId, targetOrder);
        onChanged();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      }
    },
    [onChanged],
  );

  const onFilterChange = useCallback(
    (column: string, raw: string) => {
      const next = { ...state.filters };
      if (raw === "") delete next[column];
      else next[column] = raw;
      replace({ filters: next });
    },
    [replace, state.filters],
  );

  const onAssignArticle = useCallback(
    async (
      chainRootId: number,
      targetGroupId: number | null,
      targetCategoryId: number | null,
    ) => {
      try {
        // Contract: caller passes ``targetGroupId`` non-null for "drop
        // into group" (server derives the category) OR
        // ``targetCategoryId`` non-null with ``targetGroupId`` null for
        // "Ungrouped in category". At least one must be non-null; the
        // backend rejects both-null with HTTP 400. Surface this as a
        // frontend error so a drag-drop logic bug is obvious instead of
        // hiding behind a silent 400.
        if (targetGroupId === null && targetCategoryId === null) {
          throw new Error(
            "onAssignArticle requires either targetGroupId or targetCategoryId",
          );
        }
        await api.assignArticleGroup(chainRootId, {
          product_group_id: targetGroupId,
          category_id: targetGroupId === null ? targetCategoryId : null,
        });
        onChanged();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      }
    },
    [onChanged],
  );

  // Build the "preservedQuery" that close × and Cancel use — strips the
  // current selection but keeps every workspace UI state.
  const preservedQuery = useMemo(() => {
    const sp = new URLSearchParams(queryString);
    sp.delete("category"); // legacy from create-mode entry; not workspace state
    sp.delete("group");
    return sp.toString();
  }, [queryString]);

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------

  const anchorCaption = useMemo(() => {
    if (state.window !== "refill" && state.window !== "stocktake") return null;
    const anchors = Array.from(revenue.groups.values())
      .map((g) => g.anchor)
      .filter((a): a is string => a !== null);
    if (anchors.length === 0) return null;
    const latest = anchors.sort().slice(-1)[0];
    return `Since last ${state.window === "refill" ? "refill" : "stocktake"}: ${latest.slice(0, 10)}`;
  }, [revenue.groups, state.window]);

  return (
    // Workspace-bounded scroll: the outer flex container fills exactly
    // the available content area (viewport minus the AppShell's admin
    // chrome). The LEFT side scrolls the catalog internally (its
    // sticky table header anchors to this scroll container via
    // ``getContainer``). The RIGHT side scrolls the detail pane
    // independently. This way:
    //   - the page never grows a stray scrollbar from the chrome
    //   - the right pane stays in view while the table scrolls
    //   - a tall pane doesn't force the page to grow either
    //
    // Height = viewport minus the AppShell chrome above/below this view.
    // The custom AppShell has NO top bar and (since the frameless change)
    // zero Content padding except a 52px top on mobile (clears the fixed
    // hamburger). So: 0px desktop, 52px mobile. COUPLED to AppShell's
    // <Content> paddingTop/paddingBottom — keep these in sync if that
    // padding changes. 100dvh matches the shell's mobile-URL-bar-safe unit.
    <div
      ref={workspaceOuterRef}
      style={{
        display: "flex",
        alignItems: "stretch",
        gap: 0,
        height: isMobile ? "calc(100dvh - 52px)" : "100dvh",
        overflow: "hidden",
      }}
    >
      {drag.isDragging && (
        <CatalogDragAutoScroller getScrollEl={() => leftScrollRef.current} />
      )}
      {/* On mobile (< md), the right pane fills the screen when open
          and the catalog is hidden. On larger screens both panes are
          visible side-by-side and scroll independently. */}
      <div
        ref={leftScrollRef}
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          overflowY: "auto",
          padding: "0 4px",
          display: isMobile && paneMode ? "none" : "block",
        }}
      >
        {/* Drift bucket lives ABOVE the table card so it's the first
            thing the operator sees, sitting above the tree/flat toggle
            + search + window controls. Hidden entirely when empty. */}
        <UncategorisedBucket rows={driftRows} onSelectArticle={onSelectArticle} />
        <Card>
          {/* Row 1: revenue-window selector + add-entity buttons. Placed
              above the mode/search row so the data-window (which scopes
              every Revenue/Profit/Margin number in the table below) reads
              as the "what am I looking at" control, and the mode/search
              row reads as the "how is it presented" control. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 12,
              flexWrap: "wrap",
            }}
          >
            <Select<RevenueWindow>
              value={state.window}
              onChange={(v) => onWindowChange(v)}
              style={{ width: 200 }}
              options={[
                // The default label explicitly mentions "Profit" so a
                // first-time user understands what the selector affects
                // without having to change it to find out.
                { value: "alltime", label: "All Time (Profit)" },
                { value: "30d", label: "Last 30 days" },
                { value: "7d", label: "Last 7 days" },
                { value: "refill", label: "Since last refill" },
                { value: "stocktake", label: "Since last stocktake" },
              ]}
            />
            {/* Show-deleted toggle. OFF by default (deleted chains hidden);
                ON exposes them with the DELETED badge on the name cell so
                the operator can audit / restore later. Client-side filter
                only — the backend already returns inactive chains in the
                chains view. */}
            <Space>
              <Text type="secondary">Show deleted</Text>
              <Switch
                // Default ON — absent filter == ON. Toggling off writes
                // "0" so the off-state survives reload.
                checked={state.filters.show_deleted !== "0"}
                onChange={(checked) =>
                  onFilterChange("show_deleted", checked ? "" : "0")
                }
                size="small"
              />
            </Space>
            <Space wrap style={{ marginLeft: "auto" }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate("/catalog/articles/new")}
              >
                Add article
              </Button>
              <Button
                icon={<PlusOutlined />}
                onClick={() => navigate("/catalog/groups/new")}
              >
                Add group
              </Button>
              <Button
                icon={<PlusOutlined />}
                onClick={() => navigate("/catalog/categories/new")}
              >
                Add category
              </Button>
            </Space>
          </div>
          {/* Row 2: tree/flat toggle + search. The "Reset view" button
              appears here only when there's a non-default sort or any
              collapsed row, so the operator has a one-click way back to
              the canonical view. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 12,
              flexWrap: "wrap",
            }}
          >
            <Segmented
              value={state.mode}
              onChange={(v) => onModeChange(v as "tree" | "flat")}
              options={[
                { value: "tree", label: "Tree" },
                { value: "flat", label: "Flat" },
              ]}
            />
            {(state.collapsed.size > 0 || state.sort !== null) && (
              <Button
                size="small"
                icon={<UndoOutlined />}
                onClick={() =>
                  replace({ collapsed: new Set(), sort: null })
                }
              >
                Reset view
              </Button>
            )}
            <Input.Search
              placeholder="Search name / barcode / group / category…"
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              allowClear
              style={{ flex: "1 1 240px", maxWidth: 360 }}
            />
            {/* View controls: column preset, per-column picker, and the
                low-stock highlight. All persisted in URL + localStorage. */}
            <Space wrap style={{ marginLeft: "auto" }}>
              <Segmented
                size="small"
                value={viewCfg.preset}
                onChange={(v) => {
                  const p = v as "pricing" | "stock" | "custom";
                  if (p === "custom") return; // not directly selectable
                  replace({ preset: p, cols: null });
                }}
                options={[
                  { value: "pricing", label: "Pricing" },
                  { value: "stock", label: "Stock" },
                  ...(viewCfg.preset === "custom"
                    ? [{ value: "custom", label: "Custom" }]
                    : []),
                ]}
              />
              <Popover
                trigger="click"
                placement="bottomRight"
                content={
                  <Checkbox.Group
                    value={visibleDataCols}
                    onChange={(vals) => {
                      const picked = vals as string[];
                      const next = DATA_COLUMN_ORDER.filter((c) =>
                        picked.includes(c),
                      );
                      const preset = detectPreset(next);
                      replace(
                        preset === "custom"
                          ? { preset: "custom", cols: next }
                          : { preset, cols: null },
                      );
                    }}
                    options={DATA_COLUMN_ORDER.map((c) => ({
                      value: c,
                      label: DATA_COLUMN_LABELS[c],
                    }))}
                    style={{ display: "flex", flexDirection: "column", gap: 4 }}
                  />
                }
              >
                <Button size="small" icon={<SettingOutlined />}>
                  Columns
                </Button>
              </Popover>
              <Popover
                trigger="click"
                placement="bottomRight"
                content={
                  <Space direction="vertical" size={10} style={{ width: 230 }}>
                    <Space>
                      <Switch
                        size="small"
                        checked={viewCfg.hlOn}
                        onChange={(c) => replace({ hlOn: c })}
                      />
                      <Text>Highlight low stock</Text>
                    </Space>
                    <div>
                      <Text
                        type="secondary"
                        style={{ fontSize: 12, display: "block", marginBottom: 4 }}
                      >
                        Baseline
                      </Text>
                      <Segmented
                        block
                        size="small"
                        value={viewCfg.hlRule}
                        onChange={(v) =>
                          replace({ hlRule: v as LowStockRule })
                        }
                        options={[
                          { value: "crate", label: "Crate size" },
                          { value: "since_delivery", label: "Since delivery" },
                        ]}
                      />
                    </div>
                    <Space>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Below
                      </Text>
                      <InputNumber<number>
                        size="small"
                        min={1}
                        max={99}
                        value={viewCfg.hlPct}
                        onChange={(v) => {
                          if (typeof v === "number" && !Number.isNaN(v))
                            replace({ hlPct: Math.round(v) });
                        }}
                        style={{ width: 72 }}
                      />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        % of baseline
                      </Text>
                    </Space>
                  </Space>
                }
              >
                <Tooltip title="Low-stock row highlight (colour only — does not change stock figures)">
                  <Button
                    size="small"
                    type={viewCfg.hlOn ? "default" : "dashed"}
                    icon={<WarningOutlined />}
                  >
                    {viewCfg.hlOn
                      ? `Low: ${viewCfg.hlPct}% ${
                          viewCfg.hlRule === "crate" ? "crate" : "delivery"
                        }`
                      : "Low: off"}
                  </Button>
                </Tooltip>
              </Popover>
            </Space>
          </div>
          {anchorCaption && (
            <Text type="secondary" style={{ marginBottom: 8, display: "block" }}>
              {anchorCaption}
            </Text>
          )}

          {error && (
            <Alert
              type="error"
              message={error}
              style={{ marginBottom: 12 }}
              closable
              onClose={() => setError(null)}
            />
          )}

          <CatalogTable
            rows={flatRows}
            compressed={compressed}
            sortColumn={state.sort}
            sortDir={state.sortDir}
            selectedArticleId={selectedArticleId}
            selectedCategoryId={selectedCategoryId}
            selectedGroupId={selectedGroupId}
            onSelectArticle={onSelectArticle}
            onSelectCategory={onSelectCategory}
            onSelectGroup={onSelectGroup}
            onSortChange={onSortChange}
            onToggleCollapse={onToggleCollapse}
            onAddArticleToGroup={onAddArticleToGroup}
            onAddGroupToCategory={onAddGroupToCategory}
            onReorderCategories={(ids) => void onReorderCategories(ids)}
            onReorderGroups={(catId, gids) => void onReorderGroups(catId, gids)}
            onMoveGroupToCategory={(gid, catId, beforeId, targetOrder) =>
              void onMoveGroupToCategory(gid, catId, beforeId, targetOrder)
            }
            onAssignArticle={(cr, gid, cid) =>
              void onAssignArticle(cr, gid, cid)
            }
            dragDisabled={state.mode === "flat"}
            gripOnlyDrag={isMobile}
            containerWidth={catalogContainerWidth}
            visibleDataCols={visibleDataCols}
            filters={state.filters}
            onFilterChange={onFilterChange}
            onAutoExpand={onAutoExpand}
            getScrollContainer={() => leftScrollRef.current}
            recipeRoots={recipeByRoot}
          />
        </Card>
      </div>
      {paneMode && (
        <CatalogRightPane
          mode={paneMode}
          rows={rows}
          categories={categories}
          groups={groups}
          stock={stock}
          recipeByRoot={recipeByRoot}
          preservedQuery={preservedQuery}
          onChanged={onChanged}
        />
      )}
    </div>
  );
};

// Layout note: the autoscroll-while-dragging effect uses the **window**
// as the scroll target now (not an inner element). The DnD autoscroller
// component above still mounts to ``scrollRef``; we'll let it look at
// ``window.scrollY`` when ``scrollRef.current === document.body`` or
// when scrollRef isn't attached to a scroll container. (The current
// implementation already falls back to ``el.scrollBy`` only if there's
// an element; window-edge autoscroll is a separate enhancement.)

function buildFlatComparator(
  sort: string | null,
  dir: "asc" | "desc",
): (a: FlatRow, b: FlatRow) => number {
  // Soft-deleted chains always sort AFTER active chains regardless of
  // the chosen sort key, so the catalog's primary view stays focused
  // on the live set and operators can scan deleted entries grouped at
  // the bottom (with their DELETED badge on the name cell).
  const activeBucket = (r: FlatRow): number => {
    if (r.kind !== "article") return 0;
    return r.article.active ? 0 : 1;
  };
  if (!sort) {
    return (a, b) => {
      if (a.kind !== "article" || b.kind !== "article") return 0;
      const ab = activeBucket(a) - activeBucket(b);
      if (ab !== 0) return ab;
      return a.article.name.localeCompare(b.article.name);
    };
  }
  const dirFactor = dir === "asc" ? 1 : -1;
  return (a, b) => {
    if (a.kind !== "article" || b.kind !== "article") return 0;
    const ab = activeBucket(a) - activeBucket(b);
    if (ab !== 0) return ab;
    const av = sortValue(a, sort);
    const bv = sortValue(b, sort);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    if (typeof av === "number" && typeof bv === "number") {
      return dirFactor * (av - bv);
    }
    return dirFactor * String(av).localeCompare(String(bv));
  };
}

/**
 * Autoscroll the catalog while dragging — reads cursor position from
 * the DnD monitor (no React re-renders) and scrolls the catalog's own
 * scroll container when the pointer is within 80px of its top/bottom
 * edge. Mounted only while a drag is active so the rAF loop doesn't
 * burn cycles otherwise.
 */
function CatalogDragAutoScroller({
  getScrollEl,
}: {
  getScrollEl: () => HTMLElement | null;
}) {
  const manager = useDragDropManager();
  useEffect(() => {
    let raf = 0;
    const EDGE_PX = 80;
    const MAX_DY = 18; // pixels per frame at the very edge
    const monitor = manager.getMonitor();
    const tick = () => {
      const offset = monitor.getClientOffset();
      const el = getScrollEl();
      if (offset && el && monitor.isDragging()) {
        const rect = el.getBoundingClientRect();
        const topDist = offset.y - rect.top;
        const botDist = rect.bottom - offset.y;
        let dy = 0;
        if (topDist < EDGE_PX) dy = -((EDGE_PX - topDist) / EDGE_PX) * MAX_DY;
        else if (botDist < EDGE_PX) dy = ((EDGE_PX - botDist) / EDGE_PX) * MAX_DY;
        if (dy !== 0) el.scrollBy({ top: dy });
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [manager, getScrollEl]);
  return null;
}

function sortValue(row: FlatRow, sort: string): number | string | null {
  if (row.kind !== "article") return null;
  const a = row.article;
  switch (sort) {
    case "name":
      return a.name;
    case "sell":
      return a.sell_price_cents;
    case "buy":
      return a.buy_price_cents;
    case "margin":
      return a.margin_cents;
    case "profit":
      return row.profit_cents;
    case "revenue":
      return row.revenue_cents;
    case "expected":
      return row.expected;
    case "mismatch":
      return row.mismatch;
    case "unit":
      return a.unit_type ?? null;
    // Stock-preset columns (from the windowed stock fetch on the row).
    case "refilled":
      return row.stock?.refilled_qty ?? null;
    case "sold":
      return row.stock?.sold_qty ?? null;
    case "counted":
      return row.stock?.counted_qty ?? a.counted_qty ?? null;
    case "mismatch_pct":
      return row.stock?.mismatch_pct ?? null;
    case "doc":
      return row.stock?.days_of_cover ?? null;
    case "last_refill":
      return row.stock?.last_refill_at ?? null;
    default:
      return null;
  }
}
